'use client'

import { useSession } from '@repo/sdk/auth'
import { useSearchParams } from 'next/navigation'
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react'

export type Step = 'callback' | 'check-email' | 'login' | 'new-user' | 'phone-number' | 'success'

interface WelcomeModalContextProps {
  additionalInfo?: string
  email: string
  header: ReactNode | string
  inquiryLoadId?: number
  isOpen: boolean
  setAdditionalInfo: (additionalInfo: string) => void
  setEmail: (additionalInfo: string) => void
  setInquiryLoadId: (inquiryLoadId: number | undefined) => void
  setIsOpen: (isOpen: boolean) => void
  setStep: (step: Step) => void
  step: Step
  subHeader?: string
}

const WelcomeModalContext = createContext<WelcomeModalContextProps>({} as WelcomeModalContextProps)

export const WelcomeModalContextProvider = ({
  children,
  session,
}: {
  children: ReactNode
  session?: null | unknown
}) => {
  const { data, update } = useSession()
  const [isOpen, setIsOpen] = useState(false)
  const [step, setStep] = useState<Step>('check-email')
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [email, setEmail] = useState('')
  const [inquiryLoadId, setInquiryLoadId] = useState<number>()

  const searchParams = useSearchParams()

  useEffect(() => {
    if (!session && searchParams.get('new_user') && step === 'check-email') {
      setIsOpen(true)
      return
    }

    if (data?.user && searchParams.get('new_user')) {
      if (!data.user.phone && step === 'check-email') {
        setStep('phone-number')
        setIsOpen(true)
      }
    }
  }, [data?.user, session, searchParams, step])

  const header = useMemo(() => {
    switch (step) {
      case 'callback':
        return "When's a good time for a call?"
      case 'check-email':
        return <span>Enter your email address</span>
      case 'login':
        return (
          <div>
            <p>Your email is already in our database</p>
          </div>
        )
      case 'new-user':
      case 'phone-number':
        return (
          <div>
            <p>Enter your phone number and get 20% off*</p>
          </div>
        )
      default:
        return ''
    }
  }, [step, data?.user])

  const subHeader = useMemo(() => {
    switch (step) {
      case 'login':
        return 'Please log in to view your quotes.'
      case 'phone-number':
        return 'Terms and Conditions apply'
      default:
        return undefined
    }
  }, [step])

  useEffect(() => {
    if (step !== 'check-email') {
      update()
    }
  }, [step])

  return (
    <WelcomeModalContext.Provider
      value={{
        additionalInfo,
        email,
        header,
        inquiryLoadId,
        isOpen,
        setAdditionalInfo,
        setEmail,
        setInquiryLoadId,
        setIsOpen,
        setStep,
        step,
        subHeader,
      }}
    >
      {children}
    </WelcomeModalContext.Provider>
  )
}

export const useWelcomeModalContext = () => {
  const context = useContext(WelcomeModalContext)

  if (!context) {
    throw new Error('useWelcomeModalContext must be used within a WelcomeModalContextProvider')
  }

  return context
}
